// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Substrate selector step renderer.
 * @author James Reid
 */

// @ts-check

// @@imports-dependencies
import { Deck } from "../../../../package/Deck.js"
import { Stock } from "../../../../package/index.js"

// @@imports-package
import { EpdmCalculator } from "../../../../package/Calculator.js"
import { MEMBRANES, SUBSTRATE_TYPES } from "../../../../package/types/Enums.js"
import { LoadingContainer, SteppedContainer } from "../../components/index.js"
// import { cloneTemplate } from "../../utils/document/clone-template.js"

// @@imports-types
import { State, stepRunnerCb } from "../../types/index.js"
import { fadeElementIn, fadeElementOut } from "../../utils/index.js"
import { App } from "../../app/index.js"
import { Layout } from "../Layout.js"

// @@body
// Declare instance of step wrapper for step renderer.
const substrateSelector = new Layout(() => {
    // 
    const { container, state } = App.instance
    const content = SteppedContainer.contentContainer()

    const substrateTypes = document.createElement("div")
    substrateTypes.classList.add("card-grid")

    state.quote.calculator ??= new EpdmCalculator(
        /** @type {Deck} */ (state.deck.client),
        /** @type {Stock} */ (state.quote.stock),
        { updateConfig: App.instance.state.pageOptions.lifecycle === "new" }
    )

    for (const substrate of Object.values(SUBSTRATE_TYPES)) {
        const substrateDetails = /** @type {HTMLTemplateElement} */
            (document.getElementById(`substrate-details-${substrate}`))
        const card = substrateDetails.content.cloneNode(true).childNodes[1]

        if (substrate === state.quote.calculator.config.substrate) {
            card.classList.add("selected")
        }

        card.addEventListener("click", () => {
            if (state.quote.calculator) {
                state.quote.calculator.config.substrate = substrate

                if (substrate === SUBSTRATE_TYPES.felt) {
                    state.quote.calculator.config.membrane =
                        MEMBRANES.fleeceBacked_1_20
                }
                else {
                    state.quote.calculator.config.membrane =
                        MEMBRANES.onePiece_1_14
                }
            }
        })

        substrateTypes.append(card)
    }

    content.append(substrateTypes)

    // change inactive from pointer events none to ignored in script and show
    // tooltip saying why greyed out. 
    // separate out into function, and run on intial load too.. 
    content.addEventListener("click", () => {
        const cards = /** @type {HTMLDivElement[]} */ (content
            .querySelectorAll("div.card.selectable"))
        for (let i = 0; i < cards.length; i++) {
            const card = cards[i]
            if (card.classList.contains("info")) { continue }
            if (card.dataset.substrate === state.quote.calculator.config.substrate) {
                card.classList.add("selected")
            }
            else { card.classList.remove("selected") }
        }
    })

    container.load(content)
}, {
    id: "substrate-selector",
    title: "Set Deck Surface",
    name: "Surface",
})

// @@exports
export { substrateSelector }
